/**
 * Reading the sound cue catalog (game-content/data/sounds.json, #260) as
 * untrusted input: each cue is checked on its own, so one bad cue never
 * silences the rest. See `SoundCatalog` in types/sound.ts.
 */
import { FieldReader, type Fields } from './content-fields';
import { SOUND_CHANNELS, type SoundCatalog, type SoundCueDefinition } from './types/sound';

export interface SoundCatalogParseResult {
    /** The well-formed cues; one with a problem is left out. */
    readonly catalog: SoundCatalog;
    /** What is wrong, one line each, led by the cue's id. */
    readonly problems: readonly string[];
}

function parseFiles(reader: FieldReader, fields: Fields): readonly string[] | undefined {
    const files = fields.files;
    if (!Array.isArray(files) || files.length === 0 || !files.every((file): file is string => typeof file === 'string' && file.trim() !== '')) {
        reader.problems.push('Files must be a list of one or more file paths.');
        return undefined;
    }
    return files;
}

function parsePitch(reader: FieldReader, fields: Fields): readonly [number, number] | undefined {
    const pitch = fields.pitch;
    if (pitch === undefined) {
        return undefined;
    }
    const isRate = (rate: unknown): rate is number => typeof rate === 'number' && Number.isFinite(rate) && rate > 0;
    const [low, high]: unknown[] = Array.isArray(pitch) && pitch.length === 2 ? pitch : [];
    if (!isRate(low) || !isRate(high)) {
        reader.problems.push('Pitch must be two playback rates above 0.');
        return undefined;
    }
    return [low, high];
}

function parseCue(reader: FieldReader, value: unknown): SoundCueDefinition | undefined {
    const fields = reader.fields(value, 'The cue');
    if (!fields) {
        return undefined;
    }
    const channel = reader.oneOf(fields, 'channel', 'Channel', SOUND_CHANNELS);
    const files = parseFiles(reader, fields);
    const volume = reader.optionalNumber(fields, 'volume', 'Volume', { min: 0, max: 1 });
    const pitch = parsePitch(reader, fields);
    const isLooping = reader.optionalBoolean(fields, 'isLooping', 'Looping');
    const maxVoices = reader.optionalNumber(fields, 'maxVoices', 'Max voices', { min: 1, isInteger: true });
    const minIntervalMs = reader.optionalNumber(fields, 'minIntervalMs', 'Min interval', { min: 0 });
    if (channel === undefined || files === undefined) {
        return undefined;
    }
    return {
        channel,
        files,
        ...(volume !== undefined ? { volume } : {}),
        ...(pitch !== undefined ? { pitch } : {}),
        ...(isLooping !== undefined ? { isLooping } : {}),
        ...(maxVoices !== undefined ? { maxVoices } : {}),
        ...(minIntervalMs !== undefined ? { minIntervalMs } : {}),
    };
}

/** Parses the catalog file; a cue with a problem is left out and reported. */
export function parseSoundCatalog(value: unknown): SoundCatalogParseResult {
    const fileReader = new FieldReader();
    const fields = fileReader.fields(value, 'The sound catalog');
    const version = fields === undefined ? undefined : fileReader.number(fields, 'version', 'Version', { min: 1, isInteger: true });
    const cueEntries = fields === undefined ? undefined : fileReader.fields(fields.cues, 'Cues');
    const cues: Record<string, SoundCueDefinition> = {};
    const problems: string[] = [...fileReader.problems];
    for (const [id, entry] of Object.entries(cueEntries ?? {})) {
        const reader = new FieldReader();
        const cue = parseCue(reader, entry);
        if (cue !== undefined && reader.problems.length === 0) {
            cues[id] = cue;
        }
        problems.push(...reader.problems.map((problem) => `${id}: ${problem}`));
    }
    return { catalog: { version: version ?? 1, cues }, problems };
}
